import React, { useState } from 'react';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { useDebounce } from 'use-debounce';
import styled from 'styled-components';

import { Tabs, Card, Tag } from 'antd';
import { AntdPageHeader, Table, colors } from '@yuhuinc/ui-library';
import moment from 'moment';

import {
  useApplicationProperties,
  useApplicationAgents,
  useApplications,
  useApplicationsStats,
} from './common/queries/useApplications';
import { getTranslatedString } from 'helpers/i18n';
import { featureEnabled } from 'helpers/ToggleBot';

const DEBOUNCE_TIME_IN_MS = 1000;

const StyledContainer = styled.div`
  padding: 0 24px;
`;

const ApplicationsTable = ({ currentUser }) => {
  const [activeKey, setActiveKey] = useState('assigned_to_me');
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue] = useDebounce(searchValue, DEBOUNCE_TIME_IN_MS);
  const [properties, setProperties] = useState([]);
  const [agents, setAgents] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('updated_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const tableParams = {
    query_key: activeKey,
    query: debouncedSearchValue,
    properties: properties,
    agents: agents,
    statuses: statuses,
    page: page,
    sort_key: sortKey,
    sort_direction: sortDirection,
  };

  const statParams = {
    query: debouncedSearchValue,
    properties: properties,
    agents: agents,
    statuses: statuses,
  };

  const { data: sites } = useApplicationProperties();
  const { data: allAgents } = useApplicationAgents();
  const { isLoading: statsLoading, data: stats } = useApplicationsStats(statParams);
  const { isLoading: applicationsLoading, data: applications } = useApplications(tableParams);

  const handleTableChange = (pagination, filters, sorter) => {
    setProperties(filters['properties']);
    setAgents(filters['agents']);
    setStatuses(filters['statuses']);
    setPage(pagination['current'] === page ? 1 : pagination['current']);
    setSortKey(sorter?.column?.key || 'updated_at');
    setSortDirection(sorter['order'] === 'ascend' ? 'asc' : 'desc');
  };

  const handleReset = () => {
    setActiveKey('assigned_to_me');
    setSearchValue('');
    setProperties([]);
    setAgents([]);
    setStatuses([]);
    setPage(1);
  };

  const columns = [
    {
      title: getTranslatedString('landlordPortal.applicationsTable.applicationStatus'),
      key: 'statuses',
      dataIndex: ['lifecycle', 'substatus'],
      sorter: false,
      filteredValue: statuses,
      filters: stats?.substatuses,
      render: (substatus, rowData) => (
        <Tag color={colors.TAG_STATUS_COLORS.application[substatus]} key="substatus">
          {rowData.lifecycle?.substatus_label}
        </Tag>
      ),
    },
    {
      title: getTranslatedString('landlordPortal.applicationsTable.leadApplicant'),
      key: 'primary_cotenant',
      dataIndex: 'primary_cotenant',
      sorter: true,
    },
    {
      title: getTranslatedString('landlordPortal.applicationsTable.property'),
      key: 'properties',
      dataIndex: ['property', 'name'],
      sorter: true,
      filterSearch: true,
      filteredValue: properties,
      filters: sites
        ?.map((site) => {
          return site.properties?.map((property) => ({
            text: property.name,
            value: property.key.split('__')[1],
          }));
        })
        ?.flat(),
    },
    {
      title: getTranslatedString('landlordPortal.applicationsTable.unit'),
      dataIndex: 'unit_number',
      sorter: false,
    },
    {
      title: getTranslatedString('landlordPortal.applicationsTable.moveInDate'),
      dataIndex: 'move_in_date',
      sorter: true,
    },
    {
      title: getTranslatedString('landlordPortal.applicationsTable.assignedAgent'),
      key: 'agents',
      dataIndex: ['assigned_agent', 'name'],
      filteredValue: agents,
      filters: allAgents?.map((agent) => ({
        text: agent.label,
        value: agent.value,
      })),
      sorter: true,
    },
    {
      title: getTranslatedString('landlordPortal.applicationsTable.lastUpdated'),
      dataIndex: 'updated_at',
      sorter: true,
      render: (updated_at) => moment(updated_at).format('YYYY-MM-DD'),
    },
  ];

  const loadTabTitle = (tab) => {
    return (
      <div>
        {getTranslatedString(`landlordPortal.applicationsTable.${tab}`)}{' '}
        {!statsLoading && `(${stats?.[tab]})`}
      </div>
    );
  };

  const tabList = [
    'assigned_to_me',
    'property_team_action',
    'applicant_action',
    'leases',
    'closed',
    'all',
  ];

  const disableInviteLeadButton =
    featureEnabled('application-management') && currentUser?.canReviewApplication === false;

  return (
    <div className="body" role="main">
      <AntdPageHeader
        onBack={() => window.history.back()}
        title={getTranslatedString('landlordPortal.applicationsTable.applications')}
      />

      <StyledContainer>
        <Tabs activeKey={activeKey} onTabClick={(key) => setActiveKey(key)}>
          {tabList.map((tab) => (
            <Tabs.TabPane tab={loadTabTitle(tab)} key={tab}>
              <Card>
                <Table
                  actionButtonLabel={getTranslatedString(
                    'landlordPortal.applicationsTable.inviteLead'
                  )}
                  actionButtonOnClick={() =>
                    browserHistory.push('/companies/applications/create_new_application')
                  }
                  searchTermOnChange={(search) => setSearchValue(search)}
                  disableInternalSearch
                  showActionButton={!disableInviteLeadButton}
                  dataSource={applications}
                  columns={columns}
                  loading={applicationsLoading}
                  onChange={handleTableChange}
                  pagination={{
                    total: statsLoading ? 1 : stats?.[activeKey],
                    hideOnSinglePage: true,
                    size: 'small',
                    current: page,
                  }}
                  resetButtonOnClick={handleReset}
                  scroll={{ x: false }}
                  rowKey="id"
                  onRow={(record) => {
                    return {
                      onClick: () => {
                        browserHistory.push(`/companies/applications/${record.id}`);
                      },
                    };
                  }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <Table
                        dataSource={record?.cotenants}
                        columns={[
                          {
                            dataIndex: 'full_name',
                            dataType: 'text',
                            key: 'full_name',
                            title: getTranslatedString('landlordPortal.applicationsTable.name'),
                          },
                          {
                            dataIndex: 'applicant_type',
                            dataType: 'text',
                            key: 'applicant_type',
                            title: getTranslatedString('landlordPortal.applicationsTable.role'),
                            render: (applicantType) =>
                              applicantType &&
                              getTranslatedString(
                                `landlordPortal.applicationManagement.applicationOverview.overview.applicants.applicantTypes.${applicantType}`
                              ),
                          },
                        ]}
                        hideActionAndSearch
                        pagination={{ hideOnSinglePage: true }}
                      />
                    ),
                  }}
                />
              </Card>
            </Tabs.TabPane>
          ))}
        </Tabs>
      </StyledContainer>
    </div>
  );
};

const mapStoreToProps = (store) => ({
  currentUser: store.userTypes,
});

export default connect(mapStoreToProps, null)(ApplicationsTable);
